/**
 * **What was read, as a person reads it** (engine W15, FR-E13 – FR-E16,
 * FR-E21): money in rupees from integer paise and back, a correction's text
 * as the field's value, the fields laid out by path for a screen, the checks
 * in the order a reviewer needs them, and the words for every state, reason,
 * source and rung.
 *
 * The record shapes and their normalisers are `extraction-shapes.ts`'s (the
 * client's, kit-free) and are re-exported here, so a screen imports one
 * module. **Money is integer paise everywhere** (FR-E16): it is formatted from
 * the integer's digits by the kit's one author of money, and read back from a
 * person's rupees as an integer — never through a float.
 */

import { formatINR } from '@fiveox/ui'

import type { Check, Correction, Evidence, ExtractionRecord, ExtractionReviewInfo, FieldValue, PasswordState, ReadingState, Rung } from './extraction-shapes'

export * from './extraction-shapes'

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null

/* ------------------------------------------------------ money in paise */

/** `123456` paise → `"1234.56"` — from the integer's digits, never a float. */
export function paiseToRupees(paise: number): string {
  if (!Number.isSafeInteger(paise)) return String(paise)
  const negative = paise < 0
  const abs = Math.abs(paise)
  const fraction = abs % 100
  const whole = (abs - fraction) / 100
  return `${negative ? '-' : ''}${whole}.${String(fraction).padStart(2, '0')}`
}

/** `123456` paise → `₹1,234.56`, grouped the Indian way by the kit's one author of money (ADR-0067). */
export function formatPaise(paise: number | null | undefined): string {
  if (paise === null || paise === undefined) return '—'
  return formatINR(paiseToRupees(paise))
}

const RUPEES = /^(-)?₹?\s*([0-9][0-9,]*)(?:\.([0-9]{1,2}))?$/

/**
 * What a person typed as rupees — `1,23,456.5`, `₹ 500`, `-12.05` — as integer
 * paise, or `null` when it is not an amount. The digits are put together as
 * text and read once as an integer, so no float is ever in between (FR-E16).
 */
export function rupeesToPaise(text: string): number | null {
  const m = RUPEES.exec(text.trim().replace(/\s+/g, ''))
  if (!m) return null
  const whole = (m[2] ?? '').replace(/,/g, '')
  if (!/^[0-9]+$/.test(whole)) return null
  const paise = Number(`${whole}${(m[3] ?? '').padEnd(2, '0')}`)
  if (!Number.isSafeInteger(paise)) return null
  return m[1] ? -paise : paise
}

/**
 * What a person typed into a correction, as the value the field holds — or
 * why it cannot be: money as integer paise, a date as `YYYY-MM-DD`, yes / no
 * as a boolean, text as text. Empty is `null`: the document does not show it.
 */
export function valueFromText(text: string, kind: FieldKind): { value: FieldValue } | { error: string } {
  const t = text.trim()
  if (!t) return { value: null }
  switch (kind) {
    case 'money': {
      const paise = rupeesToPaise(t)
      return paise === null ? { error: 'An amount in rupees, such as 1,23,456.78.' } : { value: paise }
    }
    case 'date':
      return /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(t) && !Number.isNaN(Date.parse(`${t}T00:00:00Z`)) ? { value: t } : { error: 'A date, YYYY-MM-DD.' }
    case 'boolean':
      return t === 'yes' ? { value: true } : t === 'no' ? { value: false } : { error: 'Yes or no.' }
    default:
      return { value: t }
  }
}

/* ------------------------------------------------------- field paths */

/** `items[2].amount` → `['items', 2, 'amount']` — the service's own grammar (`extractions.PATH`). */
export function pathParts(path: string): (string | number)[] {
  return Array.from(path.matchAll(/[A-Za-z_][A-Za-z0-9_]*|\[[0-9]+\]/g), (m) => (m[0].startsWith('[') ? Number(m[0].slice(1, -1)) : m[0]))
}

/** The value at a path, or `undefined` when the fields do not have it. */
export function valueAt(fields: unknown, path: string): unknown {
  let node: unknown = fields
  for (const part of pathParts(path)) {
    if (typeof part === 'number') {
      if (!Array.isArray(node) || part >= node.length) return undefined
      node = node[part]
    } else {
      const r = asRecord(node)
      if (!r || !(part in r)) return undefined
      node = r[part]
    }
  }
  return node
}

/** A field's evidence: its own path's, else the row it sits in (`items[2]`), else none. */
export function evidenceFor(evidence: Record<string, Evidence | null>, path: string): Evidence | null {
  if (evidence[path]) return evidence[path] ?? null
  const row = /^(.*\[[0-9]+\])\.[^.[\]]+$/.exec(path)
  return row && row[1] ? (evidence[row[1]] ?? null) : null
}

/**
 * How a field is shown and corrected. FR-E16 fixes the units: money is integer
 * paise, dates `YYYY-MM-DD`, quantities and rates decimal strings — so an
 * integer is money unless its name says it counts something.
 */
export type FieldKind = 'money' | 'number' | 'date' | 'text' | 'boolean' | 'empty' | 'list'

/** Names that hold a count or a position, never an amount. */
const COUNTING = /(^|_)(count|quarter|page|pages|row|rows|index|year|month|day|days|position|score|version|no)$/
/** Names that hold an amount — how an empty field is offered for correcting. */
const MONEY_NAME =
  /(amount|total|tax|cgst|sgst|igst|cess|balance|debit|credit|salary|income|deduction|paid|deducted|deposited|taxable|value|charges|round_off|discount|opening|closing|rebate|surcharge|relief|payable|exemption|allowance|perquisites|profits|subtotal|gross|net)/

const ISO_DATE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/

export function kindOf(key: string, value: unknown): FieldKind {
  if (value === null || value === undefined) return 'empty'
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') return Number.isInteger(value) && !COUNTING.test(key) ? 'money' : 'number'
  if (typeof value === 'string') return ISO_DATE.test(value) ? 'date' : 'text'
  return 'list'
}

/** What an empty field is offered as when a person fills it in: an amount by its name, else text. */
export function emptyKindOf(key: string, siblings: FieldKind[] = []): FieldKind {
  const known = siblings.find((k) => k !== 'empty' && k !== 'list')
  if (known) return known
  return MONEY_NAME.test(key) && !COUNTING.test(key) ? 'money' : 'text'
}

/**
 * Keys whose words their spelling cannot give — an acronym, a symbol (engine W17, D54 –
 * D56): `purchase_order`'s and `grn`'s own fields, `invoice`'s `original_invoice`, and the
 * parties, lines and summary those two share with `invoice` — so an invoice says them so too.
 */
const FIELD_LABELS: ReadonlyMap<string, string> = new Map([
  ['gstin', 'GSTIN'],
  ['hsn_sac', 'HSN / SAC'],
  ['hsn_summary', 'HSN summary'],
  ['gst_rate', 'GST rate'],
  ['cgst', 'CGST'],
  ['sgst', 'SGST'],
  ['igst', 'IGST'],
  ['original_invoice', 'Original invoice'],
  ['po_number', 'PO number'],
  ['po_date', 'PO date'],
  ['po_reference', 'PO reference'],
  ['grn_number', 'GRN number'],
  ['grn_date', 'GRN date'],
  ['invoice_reference', 'Supplier’s invoice or challan'],
  // W32 (D130): `books_ledger`'s entries — an acronym and a number its spelling cannot give.
  ['pan', 'PAN'],
  ['tan', 'TAN'],
  ['document_no', 'Document number'],
  // W37 (engine D164 – D168): ProGST's portal formats — the acronyms and the portal's own
  // words their spelling cannot give.
  ['recipient_gstins', 'Recipient GSTINs'],
  ['supplier_gstin', 'Supplier GSTIN'],
  ['ctin', 'Counterparty GSTIN'],
  ['doc_no', 'Document number'],
  ['doc_date', 'Document date'],
  ['doc_kind', 'Document kind'],
  ['doc_value', 'Document value'],
  ['original_doc', 'Original document'],
  ['boe_no', 'Bill of entry number'],
  ['itc_available', 'ITC available'],
  ['itc_reason', 'ITC not available because'],
  ['ims_state', 'IMS state'],
  ['gstr1_period', 'Supplier’s GSTR-1 period'],
  ['gstr1_filed_on', 'Supplier’s GSTR-1 filed on'],
  ['pos', 'Place of supply'],
  ['hsn', 'HSN / SAC'],
  ['uqc', 'UQC'],
  ['ur_type', 'Unregistered type'],
  ['inv_typ', 'Invoice type'],
  ['exp_type', 'Export type'],
  ['doc_issue', 'Documents issued'],
  ['nil', 'Nil-rated, exempt and non-GST'],
  ['order_id', 'Order ID'],
  // W38 (engine D175, D180, D181): ProGST's registers, the till and the trial balance — the
  // acronyms and the register's own words their spelling cannot give.
  ['invoice_no', 'Invoice number'],
  ['gstin_buyer', 'Buyer GSTIN'],
  ['gstin_supplier', 'Supplier GSTIN'],
  ['qty', 'Quantity'],
  ['cgst_rate', 'CGST rate'],
  ['sgst_rate', 'SGST / UTGST rate'],
  ['igst_rate', 'IGST rate'],
  ['gst_amount', 'GST amount'],
  ['is_memo', 'Memo'],
  ['inter_state', 'Inter- or intra-state'],
  ['fc_value', 'Value in foreign currency'],
  ['doc_status', 'Status'],
  ['shipping_bill_no', 'Shipping bill number'],
  ['row_issues', 'Rows not read'],
  ['my_amount', 'Item amount'],
  ['total_tax', 'Total tax'],
  ['gst_no', 'Buyer GSTIN'],
  ['rate_cell', 'Rate (as printed)'],
  ['month', 'The month’s amount'],
  ['month_heading', 'Month column'],
  ['gst_computed', 'GST computed'],
  ['gst_ledger', 'GST as per ledger'],
  ['below_total', 'Lines below the total row'],
])

/** `grand_total` → "Grand total"; `section_17_1` → "Section 17 1"; `po_number` → "PO number". */
export function fieldLabel(key: string): string {
  const known = FIELD_LABELS.get(key)
  if (known) return known
  const words = key.replace(/[_.]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key
}

/**
 * A quantity's column — a count of goods as printed, a decimal string (FR-E16): `invoice`'s
 * `quantity`, and a GRN's `ordered`, `received`, `accepted` and `rejected` with its `short`
 * and `excess` (engine W17, D56). Set right like a figure, kept as the text it was printed.
 */
const QUANTITY_KEYS: ReadonlySet<string> = new Set(['quantity', 'ordered', 'received', 'accepted', 'rejected', 'short', 'excess'])

export const isQuantity = (key: string): boolean => QUANTITY_KEYS.has(key.split('.').pop() ?? key)

/** A value as a reader sees it. */
export function fieldText(value: unknown, kind: FieldKind = kindOf('', value)): string {
  if (value === null || value === undefined) return '—'
  if (kind === 'money' && typeof value === 'number') return formatPaise(value)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  // An empty list was read and holds nothing — a 26AS with no Part VI, say — so it says so.
  if (Array.isArray(value)) return value.length ? value.map((v) => fieldText(v)).join(', ') : 'None'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export interface FieldEntry {
  path: string
  key: string
  label: string
  value: unknown
  kind: FieldKind
}

/** Scalars under one heading — the top level's, or one nested object's. */
export interface FieldSection {
  path: string | null
  title: string
  entries: FieldEntry[]
}

/** A list of records — the lines of an invoice, the transactions of a statement, Part A's quarters. */
export interface FieldTable {
  path: string
  title: string
  columns: { key: string; label: string; kind: FieldKind }[]
  rows: { index: number; path: string; entries: Record<string, FieldEntry> }[]
}

export interface FieldLayout {
  sections: FieldSection[]
  tables: FieldTable[]
}

const scalar = (v: unknown) => v === null || v === undefined || typeof v !== 'object'
/** A list holding records (not only scalars) — laid out as a table of its own. */
const recordList = (v: unknown): v is unknown[] => Array.isArray(v) && !v.every((x) => scalar(x))

/** A list of records met inside a table's row — an AIS record's breakup, a 26AS deductor's transactions (engine W18). */
interface InnerList {
  path: string
  name: string
  row: number
  value: unknown[]
}

/**
 * Flatten a row's record into `a`, `b.c` keys — a row's nested object is read across, not nested.
 * A list of records inside the row is not a cell: it is handed to `lists`, to become a table of its own.
 */
function rowEntries(rowPath: string, record: Record<string, unknown>, lists: (path: string, name: string, value: unknown[]) => void, prefix = ''): Record<string, FieldEntry> {
  const out: Record<string, FieldEntry> = {}
  for (const [key, value] of Object.entries(record)) {
    const name = prefix ? `${prefix}.${key}` : key
    const path = `${rowPath}.${name}`
    const nested = asRecord(value)
    if (nested) Object.assign(out, rowEntries(rowPath, nested, lists, name))
    else if (recordList(value)) lists(path, name, value)
    else if (Array.isArray(value) && value.length === 0) continue // nothing to show: no empty column for a row with no breakup
    else out[name] = { path, key, label: fieldLabel(name), value, kind: kindOf(key, value) }
  }
  return out
}

/** What a row is called in the title of a table nested under it: its number, and its name or code when it has one. */
function rowCaption(index: number, entries: Record<string, FieldEntry>): string {
  const named = ['name', 'code', 'source.name', 'description'].map((k) => entries[k]?.value).find((v): v is string => typeof v === 'string' && v.trim() !== '')
  return named ? `${index + 1} · ${named}` : String(index + 1)
}

/**
 * The fields laid out for a screen: the top level's scalars, then each nested
 * object as a section of its own, then each list of records as a table — and a
 * list of records inside a table's row (engine W18: an AIS record's breakup rows,
 * a 26AS deductor's transactions) as a table of its own after its parent, titled
 * by the row it belongs to. Every entry carries its **path** — the evidence key
 * and what a correction names.
 */
export function layoutFields(fields: Record<string, unknown>): FieldLayout {
  const sections: FieldSection[] = []
  const tables: FieldTable[] = []
  const top: FieldEntry[] = []

  const table = (path: string, title: string, value: unknown[]) => {
    const inner: InnerList[] = []
    const rows = value.map((row, index) => {
      const rowPath = `${path}[${index}]`
      const record2 = asRecord(row)
      const entries: Record<string, FieldEntry> = record2
        ? rowEntries(rowPath, record2, (p, name, list) => inner.push({ path: p, name, row: index, value: list }))
        : { value: { path: rowPath, key: 'value', label: 'Value', value: row, kind: kindOf('value', row) } }
      return { index, path: rowPath, entries }
    })
    const order: string[] = []
    for (const row of rows) for (const k of Object.keys(row.entries)) if (!order.includes(k)) order.push(k)
    const columns = order.map((k) => {
      const kinds = rows.map((r) => r.entries[k]?.kind ?? 'empty')
      const kind = kinds.find((x) => x !== 'empty') ?? emptyKindOf(k.split('.').pop() ?? k)
      return { key: k, label: fieldLabel(k), kind }
    })
    tables.push({ path, title, columns, rows })
    for (const list of inner) table(list.path, `${title} › ${rowCaption(list.row, rows[list.row]?.entries ?? {})} › ${fieldLabel(list.name)}`, list.value)
  }

  const walk = (record: Record<string, unknown>, prefix: string, title: string, into: FieldEntry[]) => {
    for (const [key, value] of Object.entries(record)) {
      const path = prefix ? `${prefix}.${key}` : key
      if (scalar(value)) {
        into.push({ path, key, label: fieldLabel(key), value, kind: kindOf(key, value) })
        continue
      }
      if (Array.isArray(value)) {
        if (!recordList(value)) {
          into.push({ path, key, label: fieldLabel(key), value, kind: 'list' })
          continue
        }
        table(path, title ? `${title} › ${fieldLabel(key)}` : fieldLabel(key), value)
        continue
      }
      const nested = asRecord(value)
      if (nested) {
        const section: FieldSection = { path, title: title ? `${title} › ${fieldLabel(key)}` : fieldLabel(key), entries: [] }
        sections.push(section)
        walk(nested, path, section.title, section.entries)
      }
    }
  }

  walk(fields, '', '', top)
  return { sections: [{ path: null, title: 'Fields', entries: top }, ...sections].filter((s) => s.entries.length > 0), tables }
}

/* ------------------------------------------------------------- checks */

const CHECK_RANK = (c: Check): number => (c.result === 'broken' ? (c.gating ? 0 : 1) : c.result === 'not_applicable' ? 2 : 3)

/** The failing checks first — a broken gating one before a broken advisory one — then what could not be checked, then what holds. */
export function orderChecks(checks: Check[]): Check[] {
  return checks.map((c, i) => ({ c, i })).sort((a, b) => CHECK_RANK(a.c) - CHECK_RANK(b.c) || a.i - b.i).map(({ c }) => c)
}

export const failingChecks = (checks: Check[] | null | undefined): Check[] => (checks ?? []).filter((c) => c.result === 'broken')
export const failingGating = (checks: Check[] | null | undefined): Check[] => failingChecks(checks).filter((c) => c.gating)

/** `items_sum_to_total` → "Items sum to total". */
export const checkLabel = (name: string): string => fieldLabel(name)

/* -------------------------------------------------- a field's chip */

/**
 * W24 (FR-56, C-E16): what a field's checks say of it — never a model's
 * confidence (engine FR-E28).
 *
 * - `edited` — the field was corrected and the kernel has not yet re-checked the
 *   latest revision; once it has, the chip is that recheck's, marked `edited`;
 * - `broken` — a failing **gating** check read it;
 * - `holds` — a holding (verified) check read it, and no gating one fails on it;
 * - `not_checked` — no check read it, only not-applicable ones did, or the
 *   extraction predates W24. A failing advisory check only informs: it is said
 *   beside the chip and never makes one broken, nor holds.
 */
export type ChipState = 'edited' | 'broken' | 'holds' | 'not_checked'

export interface FieldChip {
  state: ChipState
  /** Corrected: the chip then follows the recheck of the accepted view; `state` is `edited` until that recheck ran. */
  edited: boolean
  /** The checks that decided it, by name: the failing gating ones for `broken`, the holding ones for `holds`. */
  checks: string[]
  /** Advisory checks that read it and do not hold — said beside the chip; an advisory check only informs, so it never breaks one. */
  advisory: string[]
}

/** A pattern's part: a name, an index, or `null` for `[*]` — any row of the list. */
type PatternPart = string | number | null

/** `transactions[*].balance` → `['transactions', null, 'balance']`. `pathParts` reads a concrete path and drops `[*]`; this keeps it. */
function patternParts(path: string): PatternPart[] {
  return Array.from(path.matchAll(/[A-Za-z_][A-Za-z0-9_]*|\[(?:[0-9]+|\*)\]/g), (m) => (m[0] === '[*]' ? null : m[0].startsWith('[') ? Number(m[0].slice(1, -1)) : m[0]))
}

/** Whether `pattern` reads `path` — the same field, or a row or object it sits in (`lines[2]` reads `lines[2].amount`); `[*]` is any row. */
function reads(pattern: PatternPart[], path: (string | number)[]): boolean {
  return pattern.length <= path.length && pattern.every((p, i) => (p === null ? typeof path[i] === 'number' : p === path[i]))
}

/**
 * A check's fields, parsed once (C-E16): each pattern; for a `[*]` one its family —
 * the pattern up to its last `[*]`, `transactions[*]` — and the rows of that family
 * the check names concretely (`transactions[12].balance` names row 12), `null` when none.
 */
interface ReadPattern {
  parts: PatternPart[]
  family: PatternPart[] | null
  rows: Set<string> | null
}

const READ_CACHE = new WeakMap<Check, ReadPattern[] | null>()

function readPatternsOf(check: Check): ReadPattern[] | null {
  if (READ_CACHE.has(check)) return READ_CACHE.get(check) ?? null
  let read: ReadPattern[] | null = null
  if (Array.isArray(check.fields)) {
    const all = check.fields.map(patternParts)
    const concrete = all.filter((p): p is (string | number)[] => !p.includes(null))
    read = all.map((parts) => {
      if (!parts.includes(null)) return { parts, family: null, rows: null }
      const family = parts.slice(0, parts.lastIndexOf(null) + 1)
      const named = concrete.filter((c) => reads(family, c)).map((c) => rowKey(family, c))
      return { parts, family, rows: named.length > 0 ? new Set(named) : null }
    })
  }
  READ_CACHE.set(check, read)
  return read
}

/** The row a concrete path is in, by the indices it holds where `family` has `[*]` — `0,12` for `part_i[0].transactions[12]`. */
const rowKey = (family: PatternPart[], path: (string | number)[]): string => family.flatMap((p, i) => (p === null ? [String(path[i])] : [])).join(',')

/**
 * What one check says of one field: `broken`, `holds` or nothing. **Only a verified
 * check holds a field and only a broken one breaks it**: a check not applicable — of
 * whatever gating, and whatever rows it names — counts for nothing, as does one that
 * did not read the field or names no fields (C-E16, as clarified 2026-10-23). A broken
 * check that names concrete rows of a family covers **only those rows** of it, and
 * says nothing of the family's other rows; one that names only `[*]` covers every row.
 */
function verdictOf(check: Check, path: (string | number)[]): 'broken' | 'holds' | null {
  if (check.result !== 'verified' && check.result !== 'broken') return null
  const patterns = readPatternsOf(check)
  if (!patterns) return null
  for (const pattern of patterns) {
    if (!reads(pattern.parts, path)) continue
    if (check.result === 'verified') return 'holds'
    if (!pattern.family || !pattern.rows || pattern.rows.has(rowKey(pattern.family, path))) return 'broken'
  }
  return null
}

/**
 * The checks a chip is read from (D11, FR-E21): the kernel's recheck of the
 * **latest** revision once it ran — `null` before any correction, and while the
 * latest revision waits for its own. The same set the review's decision is made on.
 */
export function latestRecheck(record: Pick<ExtractionRecord, 'review'> & { view_checks?: Check[] | null }): Check[] | null {
  const review: ExtractionReviewInfo | null = record.review
  if (!review || review.revision <= 0 || review.rechecked_revision !== review.revision) return null
  return record.view_checks ?? review.rechecks ?? null
}

/**
 * **A field's chip** (W24, FR-56, D72, C-E16) — derived here, never stored. `checks`
 * are the extraction's as read, `recheck` the latest revision's recheck
 * (`latestRecheck`) or `null`, `corrections` its corrections. A field's checks are
 * the recheck's once it exists, else the originals; a check names the fields it
 * read by path, `[*]` matching any row. An extraction read before W24 names none,
 * so every field is *not checked*.
 */
export function chipFor(path: string, checks: readonly Check[], recheck: readonly Check[] | null, corrections: readonly Pick<Correction, 'field_path'>[] = []): FieldChip {
  const edited = corrections.some((c) => c.field_path === path)
  if (edited && !recheck) return { state: 'edited', edited, checks: [], advisory: [] }
  const parts = pathParts(path)
  const broken: string[] = []
  const advisory: string[] = []
  const holding: string[] = []
  for (const check of recheck ?? checks) {
    const verdict = verdictOf(check, parts)
    if (verdict === 'holds') holding.push(check.name)
    else if (verdict === 'broken') (check.gating ? broken : advisory).push(check.name)
  }
  if (broken.length) return { state: 'broken', edited, checks: broken, advisory }
  if (holding.length) return { state: 'holds', edited, checks: holding, advisory }
  return { state: 'not_checked', edited, checks: [], advisory }
}

/** Whether any check says which fields it read — an extraction read before W24 (kernel 0.20.0) says none. */
export const checksNameFields = (checks: readonly Check[] | null | undefined): boolean => (checks ?? []).some((c) => Array.isArray(c.fields))

/* -------------------------------------------------------------- words */

const REVIEW_STATE_TEXT: Record<string, string> = {
  pending: 'Being routed',
  app_review: 'Reviewed in its app',
  needs_review: 'Needs review',
  accepted: 'Accepted',
  rejected: 'Rejected',
}

export const extractionStateLabel = (state: string | null | undefined): string => (state ? (REVIEW_STATE_TEXT[state] ?? fieldLabel(state)) : 'Not reviewed')

/** FR-E19's reasons, said as what a reviewer looks for. */
const REASON_TEXT: Record<string, string> = {
  checks_failed: 'A check does not add up',
  identity_weak: 'Whose document it is is not certain',
  identity_contradicted: 'Whose document it is contradicts what was expected',
  ai_recovered: 'AI read a figure the checks then confirmed',
  ai_withheld: 'AI was not used — this organisation keeps documents in India',
  // Engine W39 (D160): the plan's parsing limit is reached, so no model was asked.
  ai_limit_reached: 'AI was not used — the plan’s parsing limit is reached',
  period_mismatch: 'The period is not the one expected',
  direction_unknown: 'Sale or purchase could not be told',
  auto_accept_off: 'Automatic acceptance is off',
  // Engine W18, `form26as` — an advisory flag, shown, never a stop.
  unbooked_entries: 'An entry is not booked final — its credit may not reach the return',
  // Engine W17, `grn` — advisory: what was received differs from what was ordered.
  receipt_variance: 'What was received differs from what was ordered',
}

export const reviewReasonLabel = (reason: string): string => REASON_TEXT[reason] ?? fieldLabel(reason)

const JOB_STATUS_TEXT: Record<string, string> = {
  queued: 'Waiting to be read',
  running: 'Being read',
  completed: 'Read',
  failed: 'Not read',
  refused: 'Refused',
}

export const jobStatusLabel = (status: string): string => JOB_STATUS_TEXT[status] ?? fieldLabel(status)

/** FR-E10's codes, as what happened and what to do. */
const JOB_REASON_TEXT: Record<string, string> = {
  profile_mismatch: 'It does not look like the kind of document it was read as.',
  type_unknown: 'ProDoc could not tell what kind of document it is.',
  unsupported_layout: 'Its layout is not one ProDoc reads yet.',
  unreadable: 'The file could not be read.',
  too_large: 'It is too large to read — too many pages, pixels or bytes.',
  nothing_extracted: 'Nothing could be read from it.',
  password_incorrect: 'The password was not the file’s. Nothing was kept; ask again with the right one.',
  password_required: 'It is encrypted — ask again with its password.',
  timeout: 'Reading it took longer than allowed.',
  infra: 'ProDoc could not finish reading it. Try again in a while.',
  // W32 (FR-E49, D130): a grid no saved map fits — never guessed.
  mapping_needed: 'ProDoc does not know this file’s columns yet. Map this file’s columns once; it is read the same way next time.',
}

/** D130: the refusal's copy, as the mapper and its button say it. */
export const MAPPING_NEEDED_COPY = 'Map this file’s columns once; it is read the same way next time.'

/** W19 (FR-E70, D200): a held job's `waiting` — it waits for ProDoc's own model, in India. */
export const WAITING_INHOUSE = 'inhouse_model'

/** W19 (D200): the job line while an India-only document waits for ProDoc's own AI model. */
export const waitingInhouseText = (as: string): string =>
  `Waiting for ProDoc’s own AI model, which runs in India, to read it as ${as}. It carries on by itself.`

export const jobReasonText = (reason: string | null | undefined): string => (reason ? (JOB_REASON_TEXT[reason] ?? `${fieldLabel(reason)}.`) : '')

/** W22 (FR-55): what happened to a stored file, in five words — `reading.state` as a person says it. */
export const READING_WORDS: Record<ReadingState, string> = {
  none: 'Stored',
  reading: 'Reading',
  needs_review: 'Needs review',
  accepted: 'Accepted',
  failed: 'Failed',
}

export const readingWord = (state: ReadingState | null | undefined): string => (state ? READING_WORDS[state] : '')

/** `reading.reason` in a few words, beside the five — the full sentence is `jobReasonText`'s. */
const READING_REASON_TEXT: Record<string, string> = {
  password_required: 'Needs a password',
  password_incorrect: 'Wrong password',
  app_review: 'For its app to review',
  rejected: 'Rejected by a reviewer',
  profile_mismatch: 'Not the kind of document asked for',
  type_unknown: 'Kind of document not recognised',
  unsupported_layout: 'Layout not read yet',
  unreadable: 'Could not be read',
  too_large: 'Too large to read',
  nothing_extracted: 'Nothing could be read',
  timeout: 'Took too long',
  infra: 'Not finished — try again later',
  mapping_needed: 'Its columns need mapping',
}

export const readingReasonText = (reason: string | null | undefined): string => (reason ? (READING_REASON_TEXT[reason] ?? fieldLabel(reason)) : '')

/** W22 (FR-58): the *Password* column's words. */
export const PASSWORD_WORDS: Record<PasswordState, string> = {
  not_required: 'Not required',
  locked: 'Locked',
  wrong: 'Wrong password — try again',
  unlocked: 'Unlocked',
  saved: 'Saved',
}

/** The *Unlocked* tooltip (FR-58): reading it changed nothing in the store. */
export const UNLOCKED_NOTE = 'Read with its password. The file is kept exactly as uploaded — still protected; its download needs the password.'

/** W30 (FR-67): the *Saved* tooltip — the password is kept sealed and used for the reader, never shown; the file stays as uploaded. */
export const SAVED_NOTE = 'Its password is saved, sealed: it opens for you here, and nobody is shown it. The file stays protected — its download still needs the password.'

/** W30 (FR-67): why a password was not saved — the service's `password_error` or a refusal's reason, as a person says it. */
export const PASSWORD_ERROR_TEXT: Record<string, string> = {
  password_incorrect: 'That password doesn’t open this file.',
  not_encrypted: 'This file doesn’t need a password.',
  password_unsupported: 'ProDoc saves passwords for PDFs only.',
  password_already_saved: 'A password is already saved for this file.',
}

export const passwordErrorText = (reason: string | null | undefined): string => (reason ? (PASSWORD_ERROR_TEXT[reason] ?? 'The password was not saved.') : '')

const SOURCE_TEXT: Record<string, string> = {
  text_layer: 'Text layer',
  table: 'Table',
  spreadsheet: 'Spreadsheet',
  json: 'JSON',
  text_export: 'Text export',
  ocr: 'OCR',
  ai_text: 'AI, from the text',
  ai_vision: 'AI, from the image',
  derived: 'Calculated',
}

export const evidenceSourceLabel = (source: string | null | undefined): string => (source ? (SOURCE_TEXT[source] ?? fieldLabel(source)) : 'Unknown')

/**
 * One line for where a field was read — "Text layer · page 2", "Spreadsheet · Sheet1!B7",
 * "JSON · /partB/tdsDetails/0/amount", "Text export · line 17", "Calculated · from subtotal, cgst".
 * A JSON pointer and a text line (engine W18) are said like a cell: there is no page to draw.
 */
export function evidenceText(evidence: Evidence | null | undefined, field?: string): string {
  if (!evidence) return 'No evidence recorded'
  const parts = [evidenceSourceLabel(evidence.source)]
  if (evidence.page !== null) parts.push(`page ${evidence.page}`)
  if (evidence.cell) parts.push(evidence.cell)
  // W32 (FR-E50): a grid row read under a map — "Spreadsheet · Ledger!D7" for the field's own cell, else "Spreadsheet · Ledger · row 7".
  const own = field ? evidence.cells?.[field] : undefined
  if (own && !evidence.cell) parts.push(evidence.sheet ? `${evidence.sheet}!${own}` : own)
  else if (typeof evidence.row === 'number' && !evidence.cell) {
    if (evidence.sheet) parts.push(evidence.sheet)
    parts.push(`row ${evidence.row}`)
  }
  if (typeof evidence.pointer === 'string') parts.push(evidence.pointer === '' ? 'the whole file' : evidence.pointer)
  if (typeof evidence.line === 'number') parts.push(`line ${evidence.line}`)
  if (evidence.inputs?.length) parts.push(`from ${evidence.inputs.join(', ')}`)
  if (evidence.reason) parts.push(evidence.reason === 'unparsed' ? 'could not be read' : fieldLabel(evidence.reason).toLowerCase())
  return parts.join(' · ')
}

const RUNG_TEXT: Record<string, string> = {
  static: 'Static reading',
  cache: 'The layout library',
  ai_text: 'AI on the text',
  ai_strong: 'AI, a stronger model',
  ai_vision: 'AI on the image',
}

export const rungLabel = (rung: string): string => RUNG_TEXT[rung] ?? fieldLabel(rung)

const OUTCOME_TEXT: Record<string, string> = {
  adopted: 'adopted',
  not_better: 'not adopted — the checks did not improve',
  no_answer: 'no answer',
  apply_refused: 'not adopted — its answer did not fit',
  withheld: 'not run — this organisation keeps documents in India',
  unavailable: 'not run — no AI is configured',
}

export function rungText(rung: Rung): string {
  const outcome = rung.outcome ? (OUTCOME_TEXT[rung.outcome] ?? fieldLabel(rung.outcome).toLowerCase()) : rung.adopted ? 'adopted' : rung.ran ? 'ran' : 'not run'
  return rung.calls > 0 ? `${outcome} · ${rung.calls} call${rung.calls === 1 ? '' : 's'}` : outcome
}

/** W32 (FR-E47): how a layout was read, in a person's words. */
const READER_TEXT: Record<string, string> = {
  document: 'As a document',
  structured: 'As its published format — no model',
  mapped: 'Under a column map — no model',
}

export const readerLabel = (reader: string | null | undefined): string => (reader ? (READER_TEXT[reader] ?? fieldLabel(reader)) : 'Unknown')

/** A column, 0-based, as its spreadsheet letter: 0 → `A`, 25 → `Z`, 26 → `AA`. */
export function columnLetter(index: number): string {
  let n = Math.max(0, Math.floor(index)) + 1
  let out = ''
  while (n > 0) {
    const r = (n - 1) % 26
    out = String.fromCharCode(65 + r) + out
    n = Math.floor((n - 1) / 26)
  }
  return out
}

/** A column letter as its 0-based index (`AA` → 26), or `null` when it is not one. */
export function columnIndex(letters: string): number | null {
  if (!/^[A-Z]{1,3}$/.test(letters)) return null
  let n = 0
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n - 1
}

export const channelLabel =(channel: string | null | undefined): string => (channel === 'digital' ? 'Digital' : channel === 'scanned' ? 'Scanned' : channel ? fieldLabel(channel) : 'Unknown')

/** FR-E28's identity bands. */
export const identityBandLabel = (band: string | null | undefined): string => (band ? fieldLabel(band) : 'Unknown')
